import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import { useQuery, useMutation } from "react-query";
import { useFormik } from "formik";
import Select from "react-select";
import {
  Row,
  Col,
  Input,
  Label,
  Form,
  FormText,
  FormGroup,
  FormFeedback,
  InputGroup,
  InputGroupText,
  InputGroupAddon,
  Progress,
} from "reactstrap";
import MaskedInput, { conformToMask } from "react-text-mask";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { languages, storageOptions } from "../config";
import LoadingButton from "./LoadingButton";
import InputWithLoader from "./InputWithLoader";
import { phoneMask, maskPhone, unmaskPhone } from "../utils";
import { fetchProfile, updateProfile } from "../api/actions/profile";
import { useProfile, useUpdateProfile } from "../api/hooks/profile";
import { fetcher } from "../api/client";
import PhoneNumberInput from "./PhoneNumberInput";

import i18n from "../i18n";

export const ProfileSchema = Yup.object().shape({
  givenName: Yup.string().required("Required"),
  familyName: Yup.string().required("Required"),
  representative: Yup.string(),
  companyName: Yup.string(),
  email: Yup.string().email("InvalidEmail"),
  phone: Yup.string(),
  language: Yup.string(),
  saveDocumentsOn: Yup.string(),
});

export function ProfileForm({
  onSubmit,
  onChange,
  isSubmitting,
  hideEmail = false,
  hidePhone = false,
  formik: { values, errors, touched, handleChange },
  header,
  currentLanguage,
  onLanguageChange,
  currentStorage,
  onStorageChange,
}) {
  const { t } = useTranslation("common");

  return (
    <Form onSubmit={onSubmit} noValidate>
      <Row>
        <Col lg={6} sm={6} className="mb-3">
          <FormGroup className="mb-xs-4">
            <Label for="givenName" className="font-weight-bold">
              {t("forms.firstName")} <span className="text-accent">*</span>
            </Label>
            <InputGroup>
              <InputWithLoader
                id="givenName"
                className="form-control"
                aria-describedby="emailHelp"
                placeholder={t("forms.firstName")}
                type="text"
                name="givenName"
                loading={isSubmitting}
                onChange={handleChange}
                value={values.givenName}
                invalid={errors.givenName && touched.givenName}
              />
              <FormFeedback>{t(`errors.${errors.givenName}`)}</FormFeedback>
            </InputGroup>
          </FormGroup>
        </Col>
        <Col lg={6} sm={6} className="mb-3">
          <FormGroup className="mb-xs-4">
            <Label for="givenName" className="font-weight-bold">
              {t("forms.lastName")} <span className="text-accent">*</span>
            </Label>
            <InputGroup>
              <InputWithLoader
                id="familyName"
                className="form-control"
                aria-describedby="emailHelp"
                placeholder={t("forms.lastName")}
                type="text"
                name="familyName"
                loading={isSubmitting}
                onChange={handleChange}
                value={values.familyName}
                invalid={errors.familyName && touched.familyName}
              />
              <FormFeedback>{t(`errors.${errors.familyName}`)}</FormFeedback>
            </InputGroup>
          </FormGroup>
        </Col>
        <Col lg={6} sm={6} className="mb-3">
          <FormGroup className="mb-xs-4">
            <Label for="name" className="font-weight-bold">
              {t("forms.companyName")}{" "}
            </Label>
            <InputGroup>
              <InputWithLoader
                id="companyName"
                className="form-control"
                aria-describedby="emailHelp"
                placeholder={t("forms.companyName")}
                type="text"
                name="companyName"
                loading={isSubmitting}
                onChange={handleChange}
                value={values.companyName}
                invalid={errors.companyName && touched.companyName}
              />
              <FormFeedback>{t(`errors.${errors.companyName}`)}</FormFeedback>
            </InputGroup>
          </FormGroup>
        </Col>
        <Col lg={6} sm={6} className="mb-3">
          <FormGroup className="mb-xs-4">
            <Label for="representative" className="font-weight-bold">
              {t("forms.representative")}
            </Label>
            <InputGroup>
              <InputWithLoader
                id="representative"
                className="form-control"
                aria-describedby="emailHelp"
                placeholder={t("forms.representative")}
                type="text"
                name="representative"
                loading={isSubmitting}
                onChange={handleChange}
                value={values.representative}
                invalid={errors.representative && touched.representative}
              />
              <FormFeedback>
                {t(`errors.${errors.representative}`)}
              </FormFeedback>
            </InputGroup>
          </FormGroup>
        </Col>
        {!hideEmail && (
          <Col lg={6} sm={6} className="mb-3">
            <FormGroup className="mb-0">
              <Label for="givenName" className="font-weight-bold">
                {t("forms.email")} <span className="text-accent">*</span>
              </Label>
              <InputGroup>
                <Input
                  id="email"
                  className="form-control"
                  aria-describedby="emailHelp"
                  placeholder={t("forms.email")}
                  type="email"
                  name="email"
                  disabled
                  // loading={isSubmitting}
                  onChange={handleChange}
                  value={values.email}
                  invalid={errors.email && touched.email}
                />
                <FormFeedback>{t(`errors.${errors.email}`)}</FormFeedback>
              </InputGroup>
            </FormGroup>
          </Col>
        )}
        {!hidePhone && (
          <Col lg={6} sm={6}>
            <FormGroup className="mb-xs-4">
              <Label for="givenName" className="font-weight-bold">
                {t("forms.phoneNumber")} <span className="text-accent">*</span>
              </Label>
              <InputGroup>
                <PhoneNumberInput
                  id="phone"
                  className="form-control"
                  aria-describedby="emailHelp"
                  placeholder={t("forms.phoneNumber")}
                  type="text"
                  name="phone"
                  loading={isSubmitting}
                  onChange={handleChange}
                  value={values.phone}
                  invalid={errors.phone && touched.phone}
                />
              </InputGroup>
            </FormGroup>
          </Col>
        )}
        {!hideEmail && (
          <Col lg={6} sm={6}>
            <FormGroup className="mb-xs-4">
              <Label for="givenName" className="font-weight-bold">
                {t("forms.language")} <span className="text-accent">*</span>
              </Label>
              <Select
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                className="w-100 trato-select"
                classNamePrefix="trato-select"
                onChange={onLanguageChange}
                options={languages}
                value={currentLanguage}
                defaultValue={languages?.[0]}
              />
            </FormGroup>
          </Col>
        )}
        {/* {!hideEmail && (
          <Col lg={6} sm={6}>
            <FormGroup className="mb-xs-4">
              <Label for="givenName" className="font-weight-bold">
                {t("forms.saveDocumentsOn")}{" "}
                <span className="text-accent">*</span>
              </Label>
              <Select
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                className="w-100"
                onChange={onStorageChange}
                options={storageOptions}
                value={currentStorage}
                defaultValue={storageOptions?.[0]}
              />
            </FormGroup>
          </Col>
        )} */}
      </Row>
    </Form>
  );
}

function parseData(data) {
  const office = data.office ? data.office : { representative: "", name: "" };

  return {
    ...data,
    representative: office.representative,
    companyName: office.name,
    phone: maskPhone(data.phone),
  };
}

function ProfileFormContainer() {
  const { profile, isLoading } = useProfile();
  const successToastId = "profile-updated";
  const { mutate: mutateUpdateProfile, ...updateProfileState } =
    useUpdateProfile();
  const { t } = useTranslation("common");

  const userLanguage = useMemo(() => {
    return languages.find(
      (lang) => lang.value === profile?.language?.toLowerCase?.()
    );
  }, [profile]);
  const userStorage = useMemo(() => {
    const saveOn = profile?.externalStorageConfig?.saveOn ?? "trato";

    return storageOptions.find((storage) => storage.value === saveOn);
  }, [profile]);

  const [currentLanguage, setCurrentLanguage] = useState(
    userLanguage ?? languages?.[0]
  );
  const [currentStorage, setCurrentStorage] = useState(
    userStorage ?? storageOptions?.[0]
  );
  const { handleSubmit, isSubmitting, ...formik } = useFormik({
    initialValues: parseData(profile),

    onSubmit: async (values, actions) => {
      const newProfile = {
        ...values,
        office: {
          representative: values.representative,
          name: values.companyName,
        },
        phone: unmaskPhone(values.phone),
        language: currentLanguage.value,
        externalStorageConfig: {
          ...profile.externalStorageConfig,
          saveOn: currentStorage.value,
        },
      };

      mutateUpdateProfile(newProfile);
      actions.setSubmitting();
    },
    validationSchema: ProfileSchema,
    enableReinitialize: true,
  });

  useEffect(() => {
    if (profile?.language) {
      setCurrentLanguage(
        languages.find((lang) => lang.value === profile?.language)
      );
    }
    if (profile?.externalStorageConfig?.saveOn) {
      const saveOn = profile?.externalStorageConfig?.saveOn ?? "trato";

      setCurrentStorage(
        storageOptions.find((storage) => storage.value === saveOn)
      );
    }
  }, [profile]);

  function handleLanguageChange(option, event) {
    if (event.action === "select-option") {
      setCurrentLanguage(option);
      i18n.changeLanguage(option.value);
    }
  }

  function handleStorageChange(option, event) {
    if (event.action === "select-option") {
      if (
        option.value === "dropbox" &&
        !profile?.externalStorageConfig?.dropbox?.isConnected
      ) {
        toast.success(t("Debes conectar dropbox para usar esta opcion"), {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 3000,
          toastId: successToastId,
        });
      } else {
        setCurrentStorage(option);
      }
    }
  }

  function handleLanguageChange(option, event) {
    console.log({ option, event });
    if (event.action === "select-option") {
      setCurrentLanguage(option);
      i18n.changeLanguage(option.value);
    }
  }

  return (
    <div className="card mb-3">
      <div className="card-header py-3">
        <h5 className="font-weight-bold mb-0">{t("yourDetails")}</h5>
      </div>
      <div className="card-body">
        <ProfileForm
          formik={formik}
          isSubmitting={isSubmitting || isLoading}
          onSubmit={handleSubmit}
          currentLanguage={currentLanguage}
          onLanguageChange={handleLanguageChange}
          currentStorage={currentStorage}
          onStorageChange={handleStorageChange}
        />
      </div>
      <div className="card-footer text-center">
        <FormGroup className="mb-0 py-2 align-self-end w-100">
          <LoadingButton
            type="submit"
            color="primary"
            onClick={handleSubmit}
            className="animate-up mt-xs-4"
            loadingText={t("actions.updating")}
            loading={isSubmitting || isLoading}
          >
            {t("actions.updateDetails")}
          </LoadingButton>
        </FormGroup>
      </div>
    </div>
  );
}

ProfileForm.propTypes = {};

export default ProfileFormContainer;
